import { useLocation, useMatch, useParams } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import {
  SectionHeaderSkeleton,
  ListPanelBodySkeleton,
  ListRailBodySkeleton,
  MembersPanelSkeleton,
  ReportTabsSkeleton,
  ReportBodySkeleton,
} from '../app-layout-skeleton'
import { BoardPending } from './board-pending'
import { DistributionPending } from './distribution-pending'
import { reportTabOf, type ReportTabSegment } from './report-tab-path'
import { LIST_PANE_WIDTH, PINNED_COLUMN, RAIL_WIDTH } from './pane-metrics'
import { SCROLLABLE_AREA_CLASS } from './scrollable-area-class'
import { cn, isLocalEnv } from '@/lib/utils'

/**
 * The section skeleton every list route paints at `pendingMs: 0` (jobs, offers, teams, employees, orgs). It
 * rebuilds the BOX the real section renders into — PageLayout's column, the pinned panes, the dividers —
 * so every rule lands on the pixel the real one does. `pane-metrics`, NOT `layout.tsx`: this paints before
 * its route's chunk arrives and must not drag the nav stack into the entry bundle with it.
 * DEPTH-AWARE from the URL, not a viewport read: only the SECTION route carries a pending component, so a
 * cold load of a detail url painted the section root — right on desktop, the wrong screen on a phone.
 */
export function ListPanePending() {
  return <SectionPending rail />
}

/** Administraciones' overview (orgs.index.tsx): its one action is labelled, where every toolbar's is an icon. */
export function OrgsOverviewPending() {
  const { t } = useLingui()
  return <ListPanelBodySkeleton actionLabel={t`Create administration`} />
}

/** Empleados: the one section whose list keeps its width at a detail url — no rail, no members pane, no report tabs. */
export function EmployeesListPanePending() {
  return <SectionPending strip />
}

/**
 * The icon buttons beside Empleados' search box (employees.tsx): the CSV export, then — for a viewer who can add —
 * the local-only import menu and the add button.
 */
export function employeesListActions(canAdd: boolean): number {
  return 1 + (canAdd ? (isLocalEnv ? 2 : 1) : 0)
}

/**
 * `rail`: at a detail url the list squashes to the icon column and a 450px members pane opens beside it
 * (jobs, offers, teams, orgs — teams.tsx and its three twins). `strip`: the list heads itself with the list
 * strip instead of a count label, and its detail url opens the report directly (Empleados).
 */
function SectionPending(props: { rail?: boolean; strip?: boolean }) {
  // `strict: false` because this one component is the pending UI for five sections; whichever id is present
  // means the url names a level below the root.
  const params = useParams({ strict: false })
  const atDetail =
    params.jobId != null ||
    params.offerId != null ||
    params.teamId != null ||
    params.groupId != null ||
    params.employeeId != null
  const squashed = atDetail && !!props.rail
  // The phone shows ONE level of the stack, and the url is what says which — the very rule SectionStack gives
  // the engine, so a deep link paints the screen it is about to open rather than the one above it.
  const tab = reportTabOf(useLocation().pathname)
  const atReportTab = tab !== undefined
  const listOffStage = atDetail && (!props.rail || atReportTab)
  const orgsOverview = useMatch({ from: '/_app/orgs/', shouldThrow: false }) !== undefined

  // `@container` because the report skeleton's every query (its padding ramp, the radar, the histogram's
  // 3→2→1 columns) reads the SECTION's width, exactly as it does under each section route's own root div.
  return (
    <div className="@container flex h-full flex-col">
      <SectionHeaderSkeleton stacked breadcrumb={atDetail} />
      {orgsOverview ? (
        // A full-page list, not a pane: orgs.tsx renders it through ScrollableContent › ScrollableArea.
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className={SCROLLABLE_AREA_CLASS}>
            <OrgsOverviewPending />
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {squashed && <RailSkeleton />}
          {/* The list pane — or, once it has squashed into the rail, the members pane that takes its place. */}
          <div
            className={cn(
              PINNED_COLUMN,
              'relative flex h-full min-h-0 shrink-0 flex-col',
              listOffStage && 'max-md:hidden',
            )}
            style={{ width: LIST_PANE_WIDTH }}
          >
            {squashed ? (
              // The person's Puesto rides under their name on Equipos and Ofertas; a Puesto's own integrants
              // would only restate the one in the title, and an org respondent has no user row to carry one.
              <MembersPanelSkeleton subtitle={params.teamId != null || params.offerId != null} />
            ) : (
              // `rule-fade-y` is the PANE DIVIDER the real list pane carries (CollapsiblePanelContent). Without it
              // the first paint of a detail url had no line down the middle, and one appeared as the route landed.
              <div className="rule-fade-y relative flex min-h-0 flex-1 flex-col">
                <ListPanelBodySkeleton
                  strip={props.strip}
                  // The viewer's permissions sit behind the session read, which a pending UI must not suspend on.
                  actions={props.strip ? employeesListActions(true) : undefined}
                  hint={!atDetail}
                />
              </div>
            )}
          </div>
          {/* The pane the url actually names, so the first paint is the whole section rather than its rail with a
            blank half beside it. `min-w-0` as ScrollableContent has it: without it a body wider than the pane
            (the board's grid) stretches this flex item instead of being clipped by the scroller inside. */}
          {atDetail && (
            <div
              className={cn('min-h-0 min-w-0 flex-1 flex-col', listOffStage || !props.rail ? 'flex' : 'hidden md:flex')}
            >
              {props.rail && <ReportTabsSkeleton />}
              {/* An item's own url opens the tabs over an EMPTY pane (jobs._list.$jobId.index.tsx and its twins
                render nothing into it), so only a tab has a body — and it is that tab's own. */}
              {tab && (
                <div className={SCROLLABLE_AREA_CLASS}>
                  <TabBodySkeleton tab={tab} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** The body each report-family tab paints, the same one that tab's own route declares as its pendingComponent. */
function TabBodySkeleton(props: { tab: ReportTabSegment }) {
  if (props.tab === 'board') return <BoardPending />
  if (props.tab === 'distribution') return <DistributionPending />
  return <ReportBodySkeleton />
}

/** The icon column a squashed list becomes, in the pane box the section's own rail occupies. */
function RailSkeleton() {
  return (
    <div
      className={cn(PINNED_COLUMN, 'rule-fade-y relative flex h-full min-h-0 shrink-0 flex-col max-md:hidden')}
      style={{ width: RAIL_WIDTH }}
    >
      <ListRailBodySkeleton />
    </div>
  )
}
